/**
 * FloatingTooltip — the design-system tooltip, drawn on top of the page.
 *
 * Why not the library Tooltip directly: it draws its bubble INSIDE the element
 * it wraps. Inside a scrolling box (the sidebar's portfolio list) that bubble
 * is clipped at the box's edges — a long name's tooltip gets cut off, or pushes
 * a sideways scrollbar into the list. This draws the same bubble into
 * document.body instead, pinned to where the target is on screen, so no
 * scrolling container can clip it.
 *
 * It keeps the library's LOOK by reusing the library's own classes
 * (`amd-tip`, `amd-tip__bubble--<side>`): an invisible `amd-tip` box is placed
 * exactly over the target, and the bubble positions itself against that box
 * just as it would in place. Same API as the library Tooltip: label, side,
 * children, className.
 *
 * The bubble cannot follow the target, so it closes on any scroll or resize.
 * Keeping it inside the window is app-wide, not done here (lib/tooltipEdgeGuard).
 *
 * `hoverOnly`: shown on mouse hover only, and hidden the moment focus goes
 * into the target — for hints on FORM FIELDS, where a bubble over the field
 * being typed in would cover what is typed (legacy disables field tooltips
 * while a field has focus). Without it, focus shows it too (for buttons).
 */
import { useCallback, useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';

import { cx } from '@lib/classNames';

import styles from './FloatingTooltip.module.css';

export function FloatingTooltip({
  label,
  side = 'bottom',
  children,
  className,
  hoverOnly = false,
}) {
  const id = useId();
  const [rect, setRect] = useState(null);

  const show = useCallback((e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setRect({ left: r.left, top: r.top, width: r.width, height: r.height });
  }, []);
  const hide = useCallback(() => setRect(null), []);

  // A fixed bubble would drift away from its target — close instead.
  useEffect(() => {
    if (!rect) return undefined;
    window.addEventListener('scroll', hide, true); // capture: any scrolling box
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [rect, hide]);

  return (
    <span
      className={cx(styles.target, className)}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={hoverOnly ? hide : show}
      onBlur={hoverOnly ? undefined : hide}
      aria-describedby={rect ? id : undefined}
    >
      {children}
      {rect && typeof document !== 'undefined'
        ? createPortal(
            <span
              className={cx('amd-tip', styles.anchor)}
              // position inline too, so the library's own .amd-tip rule can never
              // win and turn this back into an in-flow box.
              style={{
                position: 'fixed',
                left: rect.left,
                top: rect.top,
                width: rect.width,
                height: rect.height,
              }}
            >
              <span
                role="tooltip"
                id={id}
                className={cx('amd-tip__bubble', `amd-tip__bubble--${side}`)}
              >
                {label}
              </span>
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}

export default FloatingTooltip;
